import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
	SimpleChanges,
	ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	AFTER_7_DAYS,
	AFTER_90_DAYS,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Location,
	LOCATION_PARSER,
	MyLoadsTable,
	ProjectDTO,
	Quote,
	SelectedDateRange,
	TODAY,
	TOMORROW,
	YMD_TO_MOMENT,
	DIFFERENCE_IN_DAYS,
	DATE_YMD,
} from 'cargo-chief-commons';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { Moment } from 'moment';
import { TRAILER_TYPES_DB } from '@domain/constants/trailer-types';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UserSettingsService } from '@service/userSettings/user-settings.service';
import { ProductsService } from '@service/products/products.service';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-search-truck-bar',
	templateUrl: './search-truck-bar.component.html',
	styleUrls: ['./search-truck-bar.component.scss'],
	encapsulation: ViewEncapsulation.Emulated,
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchTruckBarComponent implements OnInit {
	@Input() useSearch: boolean = false;
	@Input() isFormDataAvailable: boolean = false;
	@Input() showPickupDate: boolean = true;
	@Input() multipleTrailerTypes: boolean = false;
	@Input() load: MyLoadsTable = null;
	@Input() quote: Quote = null;
	@Input() projectDTO: ProjectDTO = null;
	@Input() isEditQuote: boolean = false;
	@Input() isEditProject: boolean = false;
	@Input() useDaterange: boolean = false;
	@Input() isFullWidth: boolean = true;
	@Output() searchTruck = new EventEmitter<any>();
	@Output() isTruckSearchInProgress = new EventEmitter<boolean>();
	@Output() syncFormData = new EventEmitter<any>();
	@Input() routeSearchPath: string = 'capacity';
	@Input() initialOrigin: Location = null;
	@Input() initialDestination: Location = null;
	@Input() placeholderRequiredClass: string = '';
	@Input() allowStateSelection: boolean = true;
	@Input() isOriginRequired: boolean = true;
	@Input() isDestinationRequired: boolean = true;
	@Input() isTrailerTypesRequired: boolean = true;
	@Input() useTrailerTypesDefault: boolean = true;
	@Input() origin: string = '';
	@Input() destination: string = '';
	@Input() trailerType: string = '';
	@Input() customFormFieldClass: string = '';

	@Output() closeFullProfile = new EventEmitter<any>();

	isOriginCityMissing: boolean = false;
	isDestinationCityMissing: boolean = false;

	trailerTypes: any[] = [];
	minimumPickupDate = new Date();
	ranges: any = {
		Today: [TODAY(), TODAY()],
		Tomorrow: [TOMORROW(), TOMORROW()],
		'Next 7 Days': [TODAY(), AFTER_7_DAYS()],
		'Next 90 Days': [TODAY(), AFTER_90_DAYS()],
	};

	@Input() initialPickupDateRange = {
		start: this.ranges[`${this.userSettings.DEFAULT_PICKUP_DATE_TYPE}`][0],
		end: this.ranges[`${this.userSettings.DEFAULT_PICKUP_DATE_TYPE}`][1],
	};

	@Input() searchTruckForm = {
		destination: <Location>null,
		origin: <Location>null,
		pickupDate: new UntypedFormControl(new Date()),
		predestination: '',
		preorigin: '',
		price: null,
		trailerType: null,
		pickupDateStart: null,
		pickupDateEnd: null,
		blanketWrap: false,
		driverAssistLoading: false,
		driverAssistUnloading: false,
		eTrackStabilization: false,
		frozen: false,
		handLoadingRequired: false,
		homeDelivery: false,
		liftgateRequired: false,
		morningPickup: false,
		strapsRequired: false,
		tarpsRequired: false,
		teamService: false,
		whiteGlove: false,
		hazmat: false,
		hasTeams: false,
	};
	animationState = false;
	showPricingOptions = false;

	@ViewChild('originInput') originInput: ElementRef<HTMLElement>;
	@ViewChild('destinationInput') destinationInput: ElementRef<HTMLElement>;
	@ViewChild('trailerType') trailerTypeViewChild: MatSelect;
	multipleTrailerTypeSearch: string = '';

	fullProfileOpened: boolean = false;
	IS_HAZMAT_ENABLED: boolean = false;
	IS_TEAMS_CAPACITY_ENABLED: boolean = false;
	IS_ADVANCED_OPTIONS_ENABLED: boolean = false;

	constructor(
		private activeRoute: ActivatedRoute,
		private utilService: UtilService,
		public cargochief: Cargochief,
		public accountSettings: AccountSettingsService,
		public userSettings: UserSettingsService,
		public productsService: ProductsService
	) {
		this.trailerTypes = this.accountSettings.SEARCHABLE_TRAILER_TYPES;
		this.updatePickupDates(this.initialPickupDateRange.start, this.initialPickupDateRange.end);
		this.IS_TEAMS_CAPACITY_ENABLED =
			environment.TEAMS_CAPACITY && this.accountSettings.TEAMS_CAPACITY;
	}

	ngOnInit() {
		this.IS_HAZMAT_ENABLED = this.accountSettings.HAZMAT || this.productsService.HAZMAT;
		this.IS_ADVANCED_OPTIONS_ENABLED = this.IS_HAZMAT_ENABLED || this.IS_TEAMS_CAPACITY_ENABLED;
		if (IS_NOT_NULL_OR_UNDEFINED(this.trailerType) && IS_NOT_EMPTY(this.trailerType)) {
			if (this.multipleTrailerTypes) {
				this.searchTruckForm.trailerType = [this.trailerType];
			} else {
				this.searchTruckForm.trailerType = this.trailerType;
			}
		} else if (this.useTrailerTypesDefault) {
			this.searchTruckForm.trailerType = this.userSettings.DEFAULT_TRAILER_TYPES.map((trailer) =>
				trailer.toUpperCase()
			);
		}
		if (this.isFormDataAvailable) {
			if (!this.utilService.isNullOrUndefined(this.load)) {
				this.origin = this.load.origin;
				this.destination = this.load.destination;
				this.searchTruckForm.origin = this.load.originDTO;
				this.searchTruckForm.destination = this.load.destinationDTO;
				this.searchTruckForm.trailerType = this.load.trailerType.toUpperCase();
			} else if (!this.utilService.isNullOrUndefined(this.quote)) {
				this.origin = LOCATION_PARSER(this.quote.origin);
				this.destination = LOCATION_PARSER(this.quote.destination);
				this.searchTruckForm.origin = this.quote.origin;
				this.searchTruckForm.destination = this.quote.destination;
				this.searchTruckForm.trailerType = this.quote.equipment.toUpperCase();
			} else if (this.isEditProject && !this.utilService.isNullOrUndefined(this.projectDTO)) {
				this.origin = `${this.projectDTO.originCity}, ${this.projectDTO.originState}, ${this.projectDTO.originZip}`;
				this.destination = `${this.projectDTO.destCity}, ${this.projectDTO.destState}, ${this.projectDTO.destZip}`;
				this.searchTruckForm.trailerType = this.projectDTO.equipmentType.toUpperCase().split(',');
			}
			this.blurOriginInput();
		}

		if (this.useSearch) {
			this.activeRoute.queryParams.subscribe((params) => {
				const search = params['capacitySearch'];
				const searchJson = search && JSON.parse(search);

				if (IS_NOT_NULL_OR_UNDEFINED(params?.fullProfile) && search && IS_NOT_EMPTY(this.origin)) {
					this.fullProfileOpened = true;
					return;
				}
				if (IS_NULL_OR_UNDEFINED(params?.fullProfile) && this.fullProfileOpened) {
					this.closeFullProfile.emit();
					this.fullProfileOpened = false;
					return;
				}

				if (searchJson && searchJson.pickupDateStart && searchJson.pickupDateEnd) {
					let start = YMD_TO_MOMENT(searchJson.pickupDateStart),
						end = YMD_TO_MOMENT(searchJson.pickupDateEnd);
					if (DIFFERENCE_IN_DAYS(new Date(), start) < 1) {
						start = YMD_TO_MOMENT(DATE_YMD(new Date()));
					}
					if (DIFFERENCE_IN_DAYS(new Date(), end) < 1) {
						end = YMD_TO_MOMENT(DATE_YMD(start));
					}
					this.updatePickupDates(start, end);
					this.initialPickupDateRange = { start, end };
				}

				if (searchJson) {
					this.fullProfileOpened = IS_NOT_NULL_OR_UNDEFINED(params?.fullProfile);
					this.lookSearchTruck(searchJson);
				} else {
					this.isTruckSearchInProgress.emit(false);
				}
			});
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.initialOrigin)) {
			this.searchTruckForm.origin = this.initialOrigin;
			this.origin = LOCATION_PARSER(this.initialOrigin);
			this.blurOriginInput();
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.initialDestination)) {
			this.searchTruckForm.destination = this.initialDestination;
			this.destination = LOCATION_PARSER(this.initialDestination);
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.searchTruckForm.trailerType)) {
			return;
		}
		if (this.multipleTrailerTypes) {
			if (this.searchTruckForm.trailerType == null && this.useTrailerTypesDefault) {
				const defaultTrailerType = this.trailerTypes
					.reduce((accumulator, value) => accumulator.concat(value.value.toLowerCase()), [])
					.indexOf(TRAILER_TYPES_DB.VAN.toLowerCase());
				this.searchTruckForm.trailerType = [
					this.trailerTypes[defaultTrailerType > -1 ? defaultTrailerType : 0].dbVal,
				];
			}
		} else {
			this.searchTruckForm.trailerType = this.trailerTypes[0].value;
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (IS_NOT_NULL_OR_UNDEFINED(this.trailerType) && IS_NOT_EMPTY(this.trailerType)) {
			if (this.multipleTrailerTypes) {
				this.searchTruckForm.trailerType = [this.trailerType];
			} else {
				this.searchTruckForm.trailerType = this.trailerType;
			}
			this.trailerType = '';
		}
	}

	searchTrucks() {
		this.isOriginCityMissing = false;
		this.isDestinationCityMissing = false;
		const isOriginDataIncomplete = !this.isLocationDataComplete(this.searchTruckForm.origin);
		const isDestinationDataIncomplete = !this.isLocationDataComplete(
			this.searchTruckForm.destination
		);
		if (isOriginDataIncomplete || isDestinationDataIncomplete) {
			const errorMessage =
				'Please select ' + (isOriginDataIncomplete ? 'origin' : 'destination') + ' to search';
			this.utilService.showSnackbar(errorMessage, SNACKBAR.ERROR, SNACKBAR.ACTION);
			// this.searchTruckForm.trailerType would be a string for single trailer type selectio
			// and an array for multiple trailer types selection
		} else if (
			this.utilService.isNullOrUndefined(this.searchTruckForm.trailerType) ||
			this.searchTruckForm.trailerType.length == 0
		) {
			this.utilService.showSnackbar(
				'Please select a trailer type',
				SNACKBAR.ERROR,
				SNACKBAR.ACTION
			);
		} else {
			const formData = this.utilService.getTruckSearchObject(this.searchTruckForm);
			if (this.multipleTrailerTypes) {
				this.updateTrailerTypeValue(formData);
			}

			if (this.useSearch) {
				this.utilService.setCapacitySearchQueryParams(
					this.getFormDataWithoutPickupDate(formData),
					this.routeSearchPath
				);
			} else {
				const { pickupDateStart, pickupDateEnd, ...formDataWihtoutStartEndDate } = formData;
				this.searchTruck.emit(formDataWihtoutStartEndDate);
			}
		}
	}

	getAddressOnChange($event, LocationCtrl) {
		if (LocationCtrl === 'origin') {
			if (IS_NOT_NULL_OR_UNDEFINED($event.city)) {
				this.originInput.nativeElement.style.color = 'black';
				this.searchTruckForm.origin = $event;
				this.isOriginCityMissing = false;
			} else {
				this.searchTruckForm.origin = null;
				(this.originInput.nativeElement as HTMLInputElement).value = '';
			}
		} else {
			if (IS_NOT_NULL_OR_UNDEFINED($event.city)) {
				this.destinationInput.nativeElement.style.color = 'black';
				this.searchTruckForm.destination = $event;
				this.isDestinationCityMissing = false;
			} else {
				this.searchTruckForm.destination = null;
				(this.destinationInput.nativeElement as HTMLInputElement).value = '';
			}
		}
		this.syncData();
	}

	syncData() {
		if (!this.useSearch && !this.useDaterange) {
			const { pickupDateStart, pickupDateEnd, ...searchTruckFormWihtoutStartEndDate } =
				this.searchTruckForm;
			this.syncFormData.emit(searchTruckFormWihtoutStartEndDate);
		}
		if (this.useDaterange) {
			this.syncFormData.emit(this.searchTruckForm);
		}
	}

	intialiseTrailerTypes(search: any) {
		this.searchTruckForm.trailerType = search.trailerType.split(',');
	}

	lookSearchTruck(search: any) {
		if (this.useSearch || (!this.useSearch && IS_NOT_EMPTY(search.origin.city))) {
			this.origin = LOCATION_PARSER(search.origin);
			this.searchTruckForm.origin = search.origin;
		}
		if (this.useSearch || (!this.useSearch && IS_NOT_EMPTY(search.destination.city))) {
			this.destination = LOCATION_PARSER(search.destination);
			this.searchTruckForm.destination = search.destination;
		}
		this.intialiseTrailerTypes(search);
		this.populatePricingOptions(search);

		const formData = this.utilService.getTruckSearchObject(this.searchTruckForm);
		if (this.multipleTrailerTypes) {
			this.updateTrailerTypeValue(formData);
		}

		this.searchTruck.emit(this.getFormDataWithoutPickupDate(formData));

		if (this.multipleTrailerTypes) {
			if (search.trailerType) {
				let trailerTypeSplit = search.trailerType.split(',');
				let trailerTypesList = [];
				trailerTypeSplit.map((trailerType) => {
					trailerTypesList.push(trailerType);
				});
				this.searchTruckForm.trailerType = trailerTypesList;
			}
		} else {
			this.searchTruckForm.trailerType = search.trailerType;
		}
	}

	updateTrailerTypeValue(formData: any) {
		formData['trailerType'] = this.searchTruckForm.trailerType.join(',');
	}

	isLocationDataComplete(location: Location): boolean {
		// for state search
		if (location?.country && location?.state && location?.timezone) {
			return true;
		}
		// for topSearches or new search
		return !!(location?.state && location?.latitude && location?.longitude);
	}

	dateRangeUpdated(event: SelectedDateRange) {
		if (event.startDate && event.endDate) {
			this.updatePickupDates(event.startDate, event.endDate);
		}
	}

	updatePickupDates(startDate: Moment, endDate: Moment): void {
		this.searchTruckForm.pickupDateStart = startDate.format('YYYY-MM-DD');
		this.searchTruckForm.pickupDateEnd = endDate.format('YYYY-MM-DD');
		this.syncData();
	}

	getFormDataWithoutPickupDate(formData) {
		const { pickupDate, ...formDataWithoutPickupDate } = formData;
		return formDataWithoutPickupDate;
	}

	swapLocations() {
		const temp = this.searchTruckForm.origin;
		this.searchTruckForm.origin = this.searchTruckForm.destination;
		this.searchTruckForm.destination = temp;
		this.origin = LOCATION_PARSER(this.searchTruckForm.origin);
		this.destination = LOCATION_PARSER(this.searchTruckForm.destination);
		this.syncData();
		this.searchTrucks();
	}

	animate() {
		this.animationState = false;
		setTimeout(() => {
			this.animationState = true;
		}, 0);
	}

	detect(searchTerm: string) {
		if (!Array.isArray(this.searchTruckForm.trailerType)) {
			this.searchTruckForm.trailerType = [];
		}
		const result = this.utilService.detect(searchTerm, this.searchTruckForm.trailerType);
		this.searchTruckForm.trailerType = [...result];
		this.syncData();
		this.searchTruckForm.trailerType = this.searchTruckForm.trailerType.filter((trailer) => {
			return trailer;
		});
	}

	@HostListener('document:keydown.enter', ['$event']) onKeydownHandler(event: KeyboardEvent) {
		event.stopPropagation();
		if (this.useSearch) {
			if (
				this.originInput.nativeElement != document.activeElement &&
				this.destinationInput.nativeElement != document.activeElement &&
				!this.trailerTypeViewChild.focused
			) {
				this.searchTrucks();
			}
		}
	}

	togglePricingOptions() {
		this.showPricingOptions = !this.showPricingOptions;
	}

	populatePricingOptions(search) {
		this.searchTruckForm.blanketWrap = search.blanketWrap;
		this.searchTruckForm.driverAssistLoading = search.driverAssistLoading;
		this.searchTruckForm.driverAssistUnloading = search.driverAssistUnloading;
		this.searchTruckForm.eTrackStabilization = search.eTrackStabilization;
		this.searchTruckForm.frozen = search.frozen;
		this.searchTruckForm.handLoadingRequired = search.handLoadingRequired;
		this.searchTruckForm.homeDelivery = search.homeDelivery;
		this.searchTruckForm.liftgateRequired = search.liftgateRequired;
		this.searchTruckForm.morningPickup = search.morningPickup;
		this.searchTruckForm.strapsRequired = search.strapsRequired;
		this.searchTruckForm.tarpsRequired = search.tarpsRequired;
		this.searchTruckForm.teamService = search.teamService;
		this.searchTruckForm.whiteGlove = search.whiteGlove;
		this.searchTruckForm.hazmat = search.hazmat;
		this.searchTruckForm.hasTeams = search.hasTeams;
	}

	blurOriginInput(): void {
		setTimeout(() => {
			if (!this.utilService.isNullOrUndefined(this.originInput.nativeElement)) {
				this.originInput.nativeElement.blur();
			}
		}, 400);
	}

	refineSearch() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.searchTruckForm.destination.city)) {
			this.isDestinationCityMissing = false;
		} else {
			this.isDestinationCityMissing = true;
			this.destinationInput.nativeElement.focus();
		}

		if (IS_NOT_NULL_OR_UNDEFINED(this.searchTruckForm.origin.city)) {
			this.isOriginCityMissing = false;
		} else {
			this.isOriginCityMissing = true;
			this.originInput.nativeElement.focus();
		}
	}
}
